import GenericToolbar from "components/toolbars/GenericToolbar";
import Page from "design/pages/Page";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import { t } from "i18next";
import React, {
	useCallback,
	useEffect,
	useRef,
	useState,
	useMemo,
} from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useAppContext } from "qpr-base/context/AppCtxProvider";
import { getElementName } from "qpr-base/tplutils/utils";
import useUsermanagement, {
	type Group,
} from "qpr-base/apihooks/useUsermanagement";
import useSearchObjects from "backend/hooks/elements/useSearchObjects";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import useCreateAction from "backend/hooks/actions/useCreateAction";
import useAllActions from "backend/hooks/actions/useAllActions";
import useActionById from "backend/hooks/actions/useActionById";
import useUpdateAction from "backend/hooks/actions/useUpdateAction";
import useUpdateActionStatus from "backend/hooks/actions/useUpdateActionStatus";
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Button,
	Chip,
	Dialog,
	DialogTitle,
	DialogContent,
	IconButton,
	TableSortLabel,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	InputAdornment,
	CircularProgress,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import useProjectKPIQueries from "../kpisandprojectsmanagementforms/ProjectAndKPIQueries";

const useStyles = makeThemedStyles("ActionsPage", () => ({
	listContainer: {
		padding: "20px",
	},
	tableContainer: {
		maxHeight: "calc(100vh - 200px)",
		overflow: "auto",
	},
	tableRow: {
		cursor: "pointer",
		"&:hover": {
			backgroundColor: "#f5f5f5",
		},
	},
	headerCell: {
		fontWeight: 600,
		backgroundColor: "#fafafa",
	},
	actionButtons: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: "16px",
		flexWrap: "wrap",
		gap: "12px",
	},
	newButton: {
		backgroundColor: "#1976d2",
		color: "#fff",
		"&:hover": {
			backgroundColor: "#1565c0",
		},
	},
	searchFilterContainer: {
		display: "flex",
		alignItems: "center",
		gap: "16px",
		flexWrap: "wrap",
		marginBottom: "16px",
		padding: "16px",
		backgroundColor: "#fafafa",
		borderRadius: "8px",
	},
	searchField: {
		flex: 1,
		minWidth: "200px",
		maxWidth: "400px",
	},
	filterField: {
		minWidth: "150px",
		maxWidth: "200px",
	},
	pageTitle: {
		margin: 0,
		fontSize: "1.5rem",
		fontWeight: 600,
		color: "#1a1a1a",
	},
	statusChip: {
		fontWeight: 500,
	},
	statusNotStarted: {
		backgroundColor: "#f5f5f5",
		color: "#9e9e9e",
	},
	statusInProgress: {
		backgroundColor: "#e3f2fd",
		color: "#1976d2",
	},
	statusDelayed: {
		backgroundColor: "#ffebee",
		color: "#d32f2f",
	},
	statusCompleted: {
		backgroundColor: "#e8f5e9",
		color: "#2e7d32",
	},
	scrollContent: {
		overflow: "auto",
		padding: "20px",
		maxWidth: "100%",
		maxHeight: "calc(100vh - 140px)",
		minHeight: "200px",
		boxSizing: "border-box",
	},
	form: {
		maxWidth: "800px",
		width: "100%",
		boxSizing: "border-box",
	},
	field: {
		marginBottom: "16px",
	},
	label: {
		display: "block",
		marginBottom: "4px",
		fontWeight: 500,
	},
	input: {
		width: "100%",
		padding: "8px 12px",
		border: "1px solid #ccc",
		borderRadius: "4px",
		fontSize: "1rem",
		boxSizing: "border-box",
	},
	textarea: {
		width: "100%",
		padding: "8px 12px",
		border: "1px solid #ccc",
		borderRadius: "4px",
		fontSize: "1rem",
		boxSizing: "border-box",
		minHeight: "100px",
		resize: "vertical",
		fontFamily: "inherit",
	},
	groupsDropdown: {
		width: "100%",
		marginTop: "4px",
		minHeight: "56px",
	},
// strategyElementDebug: removed after fix

	fieldHint: {
		display: "block",
		marginTop: "4px",
		fontSize: "0.75rem",
		color: "#6b7280",
	},
	actions: {
		display: "flex",
		flexWrap: "wrap",
		gap: "12px",
		marginTop: "24px",
	},
	button: {
		padding: "10px 20px",
		border: "1px solid #ccc",
		borderRadius: "4px",
		backgroundColor: "#fff",
		fontSize: "1rem",
		fontWeight: 500,
		cursor: "pointer",
	},
	buttonPrimary: {
		backgroundColor: "#1976d2",
		color: "#fff",
		borderColor: "#1976d2",
	},
	buttonSecondary: {
		backgroundColor: "#f5f5f5",
		color: "#333",
		borderColor: "#ccc",
	},
	error: {
		color: "#d32f2f",
		marginBottom: "16px",
	},
	loadingContainer: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		padding: "40px",
		color: "#666",
	},
	errorContainer: {
		padding: "20px",
		backgroundColor: "#ffebee",
		borderRadius: "8px",
		marginBottom: "16px",
		color: "#c62828",
	},
	errorTitle: {
		fontWeight: 600,
		marginBottom: "8px",
	},
	dialogPaper: {
		maxWidth: "600px",
		width: "90%",
		maxHeight: "90vh",
	},
	dialogTitle: {
		padding: "16px 24px",
		borderBottom: "1px solid #e0e0e0",
	},
	dialogContent: {
		padding: "24px",
		overflow: "auto",
	},
}));

interface UserOrGroup {
	id: string;
	name: string;
	pretty_name?: string;
	fullname?: string;
}

interface KpiOrInitiative {
	id: string;
	typename: "KPI" | "Initiative";
	name: string;
	displayName: string;
}

interface ActionItem {
	id: string;
	actionName: string;
	description: string;
	deadline: string;
	createdAt: string;
	status: "Not Started" | "In Progress" | "Delayed" | "Completed";
	scorecard: string;
	strategyElement: string;
	searchKpiInitiativeId: string | null;
}

function groupPerspectivesBySymbol(rows: any[]): Record<string, any[]> {
	const out: Record<string, any[]> = {};
	for (const r of rows) {
		const s = r.symbol || "";
		if (!out[s]) out[s] = [];
		out[s].push(r);
	}
	return out;
}

function strategicObjectiveChildIdSet(perspectives: any[]): Set<string> {
	const ids = new Set<string>();
	for (const p of perspectives) {
		const parts = String(p.childobjects || "")
			.split(",")
			.map((x) => x.trim())
			.filter(Boolean);
		for (const id of parts) ids.add(id);
	}
	return ids;
}

/** All element ids under given Strategic Objective nodes (via childobjects), including roots */
function buildDescendantIdsUnderStrategicObjectives(
	hierarchyElements: any[],
	objectiveSymbol: string,
	scorecardTopId: string
): Set<string> {
	const roots = hierarchyElements.filter(
		(d: any) =>
			d.typename === "Strategic Objective" &&
			d.symbol === objectiveSymbol &&
			(!scorecardTopId || d.topelementid === scorecardTopId)
	);
	const byId = new Map(hierarchyElements.map((e: any) => [e.id, e]));
	const out = new Set<string>();
	const stack = roots.map((r: any) => r.id);
	while (stack.length) {
		const id = stack.pop()!;
		if (out.has(id)) continue;
		out.add(id);
		const node = byId.get(id);
		const raw = node?.childobjects;
		if (!raw) continue;
		for (const cid of String(raw)
			.split(",")
			.map((x: string) => x.trim())
			.filter(Boolean)) {
			if (!out.has(cid)) stack.push(cid);
		}
	}
	return out;
}

function linkedElementMatchesHierarchyFilters(
	linkedId: string | null | undefined,
	hierarchyElements: any[],
	scorecardFilter: string,
	perspectiveFilter: string,
	objectiveFilter: string,
	passWhenNotInHierarchy = true
): boolean {
	if (!scorecardFilter && !perspectiveFilter && !objectiveFilter) {
		return true;
	}
	if (!linkedId) {
		return false;
	}
	const byId = new Map(hierarchyElements.map((e: any) => [e.id, e]));
	const el = byId.get(linkedId);
	if (!el) {
		return passWhenNotInHierarchy;
	}
	if (scorecardFilter && el.topelementid !== scorecardFilter) {
		return false;
	}
	if (!perspectiveFilter && !objectiveFilter) {
		return true;
	}
	let okPerspective = !perspectiveFilter;
	let okObjective = !objectiveFilter;
	if (objectiveFilter && el) {
		const underObjective = buildDescendantIdsUnderStrategicObjectives(
			hierarchyElements,
			objectiveFilter,
			scorecardFilter
		);
		if (underObjective.has(el.id)) {
			okObjective = true;
		}
	}
	let current: any = el;
	const visited = new Set<string>();
	while (current && !visited.has(current.id)) {
		visited.add(current.id);
		if (
			perspectiveFilter &&
			current.typename === "Perspective" &&
			current.symbol === perspectiveFilter
		) {
			okPerspective = true;
		}
		if (
			objectiveFilter &&
			current.typename === "Strategic Objective" &&
			current.symbol === objectiveFilter
		) {
			okObjective = true;
		}
		const pid = current.parentelementid;
		current = pid ? byId.get(pid) : undefined;
	}
	if (perspectiveFilter && !okPerspective) return false;
	if (objectiveFilter && !okObjective) return false;
	return true;
}

function actionMatchesLocationFilters(
	action: ActionItem,
	hierarchyElements: any[],
	scorecardFilter: string,
	perspectiveFilter: string,
	objectiveFilter: string
): boolean {
	return linkedElementMatchesHierarchyFilters(
		action.searchKpiInitiativeId,
		hierarchyElements,
		scorecardFilter,
		perspectiveFilter,
		objectiveFilter,
		true
	);
}

function useHierarchyLocationPickers() {
	const { data: hierarchyResponse, isLoading: hierarchyLoading } =
		useProjectKPIQueries();
	const hierarchyElements = hierarchyResponse?.data ?? [];
	const [scorecardFilter, setScorecardFilter] = useState("");
	const [perspectiveFilter, setPerspectiveFilter] = useState("");
	const [objectiveFilter, setObjectiveFilter] = useState("");

	const geOptions = useMemo(() => {
		if (!hierarchyElements.length) return [];
		return hierarchyElements.filter((d: any) => d.typename === "Organization");
	}, [hierarchyElements]);

	const perspectiveRows = useMemo(() => {
		if (!scorecardFilter) return [];
		return hierarchyElements.filter(
			(d: any) =>
				d.typename === "Perspective" && d.topelementid === scorecardFilter
		);
	}, [hierarchyElements, scorecardFilter]);

	const themeOptionsBySymbol = useMemo(
		() => groupPerspectivesBySymbol(perspectiveRows),
		[perspectiveRows]
	);

	const objectiveRows = useMemo(() => {
		const base = !scorecardFilter
			? hierarchyElements.filter(
					(d: any) => d.typename === "Strategic Objective"
			  )
			: hierarchyElements.filter(
					(d: any) =>
						d.typename === "Strategic Objective" &&
						d.topelementid === scorecardFilter
			  );
		if (!perspectiveFilter) return base;
		const perspectivesWithSymbol = themeOptionsBySymbol[perspectiveFilter] || [];
		const allowed = strategicObjectiveChildIdSet(perspectivesWithSymbol);
		if (allowed.size === 0) return base;
		return base.filter((d: any) => allowed.has(d.id));
	}, [
		hierarchyElements,
		scorecardFilter,
		perspectiveFilter,
		themeOptionsBySymbol,
	]);

	const perspectiveSelectOptions = useMemo(() => {
		const seen = new Set<string>();
		const opts: { symbol: string; row: any }[] = [];
		for (const p of perspectiveRows) {
			const sym = p.symbol || "";
			if (!sym || seen.has(sym)) continue;
			seen.add(sym);
			opts.push({ symbol: sym, row: p });
		}
		return opts;
	}, [perspectiveRows]);

	const objectiveSelectOptions = useMemo(() => {
		const seen = new Set<string>();
		const opts: { symbol: string; row: any }[] = [];
		for (const o of objectiveRows) {
			const sym = o.symbol || "";
			if (!sym || seen.has(sym)) continue;
			seen.add(sym);
			opts.push({ symbol: sym, row: o });
		}
		return opts;
	}, [objectiveRows]);

	const setScorecardFilterCascade = useCallback((v: string) => {
		setScorecardFilter(v);
		setPerspectiveFilter("");
		setObjectiveFilter("");
	}, []);

	const setPerspectiveFilterCascade = useCallback((v: string) => {
		setPerspectiveFilter(v);
		setObjectiveFilter("");
	}, []);

	return {
		hierarchyElements,
		hierarchyLoading,
		scorecardFilter,
		setScorecardFilter: setScorecardFilterCascade,
		perspectiveFilter,
		setPerspectiveFilter: setPerspectiveFilterCascade,
		objectiveFilter,
		setObjectiveFilter,
		geOptions,
		perspectiveSelectOptions,
		objectiveSelectOptions,
	};
}

interface ActionValues {
	actionName: string;
	deadline: string;
	description: string;
	assignTo: Group[];
	searchKpiInitiative: KpiOrInitiative | null;
	strategyKpiInitiative: KpiOrInitiative | null;
	status?: "Not Started" | "In Progress" | "Delayed" | "Completed";

}

interface ActionsListProps {
	onRowClick: (id: string) => void;
	onNewClick: () => void;
}

interface ActionsFormProps {
	isFullScreen?: boolean;
	onClose?: () => void;
}

const ValidationSchema = Yup.object().shape({
	actionName: Yup.string().required(t("Actions.Action name is required")),
	deadline: Yup.string().required(t("Actions.Deadline is required")),
});

const ActionsList: React.FC<ActionsListProps> = ({ onRowClick, onNewClick }) => {
	const classes = useStyles();
	const ctx = useAppContext();

	const { data: actionsData, isLoading, error } = useAllActions();
	const {
		hierarchyElements,
		hierarchyLoading,
		scorecardFilter,
		setScorecardFilter,
		perspectiveFilter,
		setPerspectiveFilter,
		objectiveFilter,
		setObjectiveFilter,
		geOptions,
		perspectiveSelectOptions,
		objectiveSelectOptions,
	} = useHierarchyLocationPickers();

	const actions: ActionItem[] = useMemo(() => {
		if (!actionsData || !Array.isArray(actionsData)) {
			return [];
		}
		return actionsData.map((action: any) => ({
			id: action._id || action.id || "",
			actionName: action.actionName || "",
			description: action.description || "",
			deadline: action.deadline || "",
			createdAt: action.createdAt || "",
			status: action.status as ActionItem["status"] || "Not Started",
			scorecard: action.scorecard || action.searchKpiInitiativeName || "",
			strategyElement: action.searchKpiInitiativeName || "",
			searchKpiInitiativeId: action.searchKpiInitiativeId ?? null,
		}));
	}, [actionsData]);

	const [searchQuery, setSearchQuery] = useState("");
	const [statusFilter, setStatusFilter] = useState("all");
	const [sortColumn, setSortColumn] = useState<"actionName" | "deadline" | "createdAt" | "status" | "scorecard" | "strategyElement">("actionName");
	const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

	const getStatusChipClass = (status: string): string => {
		const statusClasses: Record<string, string> = {
			"Not Started": classes.statusNotStarted!,
			"In Progress": classes.statusInProgress!,
			"Delayed": classes.statusDelayed!,
			"Completed": classes.statusCompleted!,
		};
		return statusClasses[status] || classes.statusNotStarted!;
	};

	const formatDate = (dateString: string): string => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const handleSort = (column: "actionName" | "deadline" | "createdAt" | "status" | "scorecard" | "strategyElement") => {
		if (sortColumn === column) {
			setSortOrder(sortOrder === "asc" ? "desc" : "asc");
		} else {
			setSortColumn(column);
			setSortOrder("asc");
		}
	};

	const filteredAndSortedActions = useMemo(() => {
		let result = [...actions];

		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase().trim();
			result = result.filter(
				(action) =>
					action.actionName.toLowerCase().includes(query) ||
					action.scorecard.toLowerCase().includes(query) ||
					(action.strategyElement || "").toLowerCase().includes(query)
			);
		}

		if (statusFilter !== "all") {
			result = result.filter((action) => action.status === statusFilter);
		}

		result = result.filter((action) =>
			actionMatchesLocationFilters(
				action,
				hierarchyElements,
				scorecardFilter,
				perspectiveFilter,
				objectiveFilter
			)
		);

		result.sort((a, b) => {
			let aValue: string | number = "";
			let bValue: string | number = "";

			switch (sortColumn) {
				case "actionName":
					aValue = a.actionName.toLowerCase();
					bValue = b.actionName.toLowerCase();
					break;
				case "deadline":
					aValue = new Date(a.deadline).getTime();
					bValue = new Date(b.deadline).getTime();
					break;
				case "createdAt":
					aValue = new Date(a.createdAt).getTime();
					bValue = new Date(b.createdAt).getTime();
					break;
				case "status":
					aValue = a.status;
					bValue = b.status;
					break;
				case "scorecard":
					aValue = a.scorecard.toLowerCase();
					bValue = b.scorecard.toLowerCase();
					break;
				case "strategyElement":
					aValue = (a.strategyElement || "").toLowerCase();
					bValue = (b.strategyElement || "").toLowerCase();
					break;
			}

			if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
			if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
			return 0;
		});

		return result;
	}, [
		actions,
		searchQuery,
		statusFilter,
		sortColumn,
		sortOrder,
		hierarchyElements,
		scorecardFilter,
		perspectiveFilter,
		objectiveFilter,
	]);

	if (isLoading) {
		return (
			<div className={classes.listContainer}>
				<div className={classes.loadingContainer}>
					Loading actions...
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className={classes.listContainer}>
				<div className={classes.errorContainer}>
					<div className={classes.errorTitle}>Error loading actions</div>
					<div>{error.message || "An unexpected error occurred"}</div>
				</div>
			</div>
		);
	}

	return (
		<div className={classes.listContainer}>
			<div className={classes.actionButtons}>
				<h2 className={classes.pageTitle}>{t("Actions.Actions")}</h2>
				<Button
					variant="contained"
					className={classes.newButton}
					startIcon={<AddIcon />}
					onClick={onNewClick}
				>
					{t("Actions.New Action")}
				</Button>
			</div>

			<div className={classes.searchFilterContainer}>
				<TextField
					className={classes.searchField}
					variant="outlined"
					size="small"
					placeholder={t("Actions.Search by Action Name or KPI Name")}
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon color="action" />
							</InputAdornment>
						),
					}}
				/>
				<FormControl className={classes.filterField} size="small">
					<InputLabel id="status-filter-label">{t("Actions.Status")}</InputLabel>
					<Select
						labelId="status-filter-label"
						value={statusFilter}
						label={t("Actions.Status")}
						onChange={(e) => setStatusFilter(e.target.value as string)}
					>
						<MenuItem value="all">{t("Actions.All")}</MenuItem>
						<MenuItem value="Not Started">{t("Actions.Not Started")}</MenuItem>
						<MenuItem value="In Progress">{t("Actions.In Progress")}</MenuItem>
						<MenuItem value="Delayed">{t("Actions.Delayed")}</MenuItem>
						<MenuItem value="Completed">{t("Actions.Completed")}</MenuItem>
					</Select>
				</FormControl>
				<FormControl
					className={classes.filterField}
					size="small"
					disabled={hierarchyLoading}
				>
					<InputLabel id="actions-scorecard-filter-label">
						{t("configtypes.Scorecard")}
					</InputLabel>
					<Select
						labelId="actions-scorecard-filter-label"
						value={scorecardFilter}
						label={t("configtypes.Scorecard")}
						onChange={(e) =>
							setScorecardFilter(e.target.value as string)
						}
					>
						<MenuItem value="">{t("Actions.All")}</MenuItem>
						{geOptions.map((ge: any) => (
							<MenuItem key={ge.id} value={ge.id}>
								{getElementName(ge, ctx.language)}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl
					className={classes.filterField}
					size="small"
					disabled={hierarchyLoading || !scorecardFilter}
				>
					<InputLabel id="actions-perspective-filter-label">
						{t("configtypes.Perspective")}
					</InputLabel>
					<Select
						labelId="actions-perspective-filter-label"
						value={perspectiveFilter}
						label={t("configtypes.Perspective")}
						onChange={(e) =>
							setPerspectiveFilter(e.target.value as string)
						}
					>
						<MenuItem value="">{t("Actions.All")}</MenuItem>
						{perspectiveSelectOptions.map(({ symbol, row }) => (
							<MenuItem key={symbol} value={symbol}>
								{getElementName(row, ctx.language)}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl
					className={classes.filterField}
					size="small"
					disabled={hierarchyLoading}
				>
					<InputLabel id="actions-objective-filter-label">
						{t("configtypes.Strategic Objective")}
					</InputLabel>
					<Select
						labelId="actions-objective-filter-label"
						value={objectiveFilter}
						label={t("configtypes.Strategic Objective")}
						onChange={(e) => setObjectiveFilter(e.target.value as string)}
					>
						<MenuItem value="">{t("Actions.All")}</MenuItem>
						{objectiveSelectOptions.map(({ symbol, row }) => (
							<MenuItem key={symbol} value={symbol}>
								{getElementName(row, ctx.language)}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</div>

			<TableContainer component={Paper} className={classes.tableContainer}>
				<Table stickyHeader>
					<TableHead>
						<TableRow>
							<TableCell className={classes.headerCell}>
								<TableSortLabel
									active={sortColumn === "actionName"}
									direction={sortColumn === "actionName" ? sortOrder : "asc"}
									onClick={() => handleSort("actionName")}
								>
									{t("Actions.Action Name")}
								</TableSortLabel>
							</TableCell>
							<TableCell className={classes.headerCell}>
								<TableSortLabel
									active={sortColumn === "strategyElement"}
									direction={sortColumn === "strategyElement" ? sortOrder : "asc"}
									onClick={() => handleSort("strategyElement")}
								>
									{t("Actions.Strategy Element")}
								</TableSortLabel>
							</TableCell>
							<TableCell className={classes.headerCell}>
								<TableSortLabel
									active={sortColumn === "scorecard"}
									direction={sortColumn === "scorecard" ? sortOrder : "asc"}
									onClick={() => handleSort("scorecard")}
								>
									{t("Actions.Scorecard")}
								</TableSortLabel>
							</TableCell>
							<TableCell className={classes.headerCell}>
								<TableSortLabel
									active={sortColumn === "status"}
									direction={sortColumn === "status" ? sortOrder : "asc"}
									onClick={() => handleSort("status")}
								>
									{t("Actions.Status")}
								</TableSortLabel>
							</TableCell>
							<TableCell className={classes.headerCell}>
								<TableSortLabel
									active={sortColumn === "deadline"}
									direction={sortColumn === "deadline" ? sortOrder : "asc"}
									onClick={() => handleSort("deadline")}
								>
									{t("Actions.Deadline")}
								</TableSortLabel>
							</TableCell>
							<TableCell className={classes.headerCell}>
								<TableSortLabel
									active={sortColumn === "createdAt"}
									direction={sortColumn === "createdAt" ? sortOrder : "asc"}
									onClick={() => handleSort("createdAt")}
								>
									{t("Actions.Created")}
								</TableSortLabel>
							</TableCell>
							<TableCell className={classes.headerCell}>{t("Actions.Description")}</TableCell>


						</TableRow>
					</TableHead>
					<TableBody>
						{filteredAndSortedActions.length > 0 ? (
							filteredAndSortedActions.map((action) => (
								<TableRow
									key={action.id}
									className={classes.tableRow}
									onClick={() => onRowClick(action.id)}
								>
									<TableCell>{action.actionName}</TableCell>
									<TableCell>{action?.strategyElement ? action?.strategyElement:"-"}</TableCell>
									<TableCell>{action.scorecard}</TableCell>
									<TableCell>
										<Chip
											label={t("Actions." + action.status.charAt(0).toUpperCase() + action.status.slice(1))}
											className={`${classes.statusChip} ${getStatusChipClass(action.status)}`}
											size="small"
										/>
									</TableCell>

									<TableCell>{formatDate(action.deadline)}</TableCell>
									<TableCell>{formatDate(action.createdAt)}</TableCell>
									<TableCell>{action.description}</TableCell>


								</TableRow>
							))
						) : (
							<TableRow>
								<TableCell colSpan={7} align="center" style={{ padding: "40px", color: "#666" }}>
									{t("Actions.No actions found")}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</div>
	);
};

const ActionsForm: React.FC<ActionsFormProps> = ({ isFullScreen, onClose }) => {
	const { meaId } = useParams<{ meaId?: string }>();
	const history = useHistory();
	const ctx = useAppContext();
	const tenant = ctx.tenant || "tec";
	const model = ctx.modelId || ctx.model;
	const classes = useStyles();

	const isCreateMode = meaId === "new";
	const isEditMode = meaId && meaId !== "new";

	const createActionMutation = useCreateAction();
	const updateActionMutation = useUpdateAction();
	const updateStatusMutation = useUpdateActionStatus();

	const { data: actionData, isLoading: isLoadingAction, error: actionError } = useActionById(
		isEditMode ? (meaId as string) : undefined
	);

	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const kpiNameFromUrl = searchParams.get("kpiName") || "";
	const kpiIdFromUrl = searchParams.get("kpiId") || "";
	const initiativeNameFromUrl = searchParams.get("initiativeName") || "";
	const initiativeIdFromUrl = searchParams.get("initiativeId") || "";
	const isFromKpiDetails = isCreateMode && !!kpiNameFromUrl;
	const isFromInitiativeDetails = isCreateMode && !!initiativeNameFromUrl;

	const {
		hierarchyElements,
		hierarchyLoading,
		scorecardFilter,
		setScorecardFilter,
		perspectiveFilter,
		setPerspectiveFilter,
		objectiveFilter,
		setObjectiveFilter,
		geOptions,
		perspectiveSelectOptions,
		objectiveSelectOptions,
	} = useHierarchyLocationPickers();

	const hierarchySeedRef = useRef<string | null>(null);

	const { fetchGroupsAndUsers } = useUsermanagement();
	const apiRef = useRef<{ fetchGroupsAndUsers: typeof fetchGroupsAndUsers }>({ fetchGroupsAndUsers });
	apiRef.current.fetchGroupsAndUsers = fetchGroupsAndUsers;

	const [initialValues, setInitialValues] = useState<ActionValues>({
		actionName: "",
		deadline: "",
		description: "",
		assignTo: [],
		searchKpiInitiative: null,
		strategyKpiInitiative: null,
	});


	const [users, setUsers] = useState<UserOrGroup[]>([]);
	const [kpisAndInitiatives, setKpisAndInitiatives] = useState<KpiOrInitiative[]>([]);
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);
	const [isEditing, setIsEditing] = useState(isCreateMode);

	const { data: searchData, isLoading: isSearchLoading } = useSearchObjects(["KPI", "Initiative"]);

	const loadData = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const { fetchGroupsAndUsers } = apiRef.current;
			const { groups: g, users: u } = await fetchGroupsAndUsers();

			const allOptions: UserOrGroup[] = [
				...(u || []).map((user: any) => ({
					id: user.id,
					name: user.fullname || user.name || "",
				})),
				...(g || []).map((group: Group) => ({
					id: group.id,
					name: group.pretty_name || group.name || "",
				})),
			];
			setUsers(allOptions);
		} catch (e: any) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadData();
	}, [loadData]);

	useEffect(() => {
		if (searchData && Array.isArray(searchData)) {
			const transformed: KpiOrInitiative[] = searchData.map((item: any) => ({
				id: item.id,
				typename: (item.typename as "KPI" | "Initiative") || "KPI",
				name: item.Name || item.name || "",
				displayName: `${item.Name || item.name || ""} - ${item.typename === "KPI" ? "KPI" : "Initiative"}`,
			}));
			setKpisAndInitiatives(transformed);
		}
	}, [searchData, isSearchLoading, isEditing]);

	useEffect(() => {
		hierarchySeedRef.current = null;
	}, [meaId, kpiIdFromUrl, initiativeIdFromUrl]);

	const filteredKpisAndInitiatives = useMemo(() => {
		if (
			!scorecardFilter &&
			!perspectiveFilter &&
			!objectiveFilter
		) {
			return kpisAndInitiatives;
		}
		return kpisAndInitiatives.filter((k) =>
			linkedElementMatchesHierarchyFilters(
				k.id,
				hierarchyElements,
				scorecardFilter,
				perspectiveFilter,
				objectiveFilter,
				false
			)
		);
	}, [
		kpisAndInitiatives,
		hierarchyElements,
		scorecardFilter,
		perspectiveFilter,
		objectiveFilter,
	]);

	useEffect(() => {
		if (!hierarchyElements.length) return;
		let linkedId = "";
		if (isEditMode && actionData?.searchKpiInitiativeId) {
			linkedId = actionData.searchKpiInitiativeId;
		} else if (isFromKpiDetails && kpiIdFromUrl) {
			linkedId = kpiIdFromUrl;
		} else if (isFromInitiativeDetails && initiativeIdFromUrl) {
			linkedId = initiativeIdFromUrl;
		}
		if (!linkedId) return;
		if (hierarchySeedRef.current === linkedId) return;
		const el = hierarchyElements.find((d: any) => d.id === linkedId);
		if (!el?.topelementid) return;
		hierarchySeedRef.current = linkedId;
		setScorecardFilter(el.topelementid);
		const byId = new Map(hierarchyElements.map((e: any) => [e.id, e]));
		let persSym = "";
		let objSym = "";
		let cur: any = el;
		const visited = new Set<string>();
		while (cur && !visited.has(cur.id)) {
			visited.add(cur.id);
			if (cur.typename === "Strategic Objective" && cur.symbol && !objSym) {
				objSym = cur.symbol;
			}
			if (cur.typename === "Perspective" && cur.symbol && !persSym) {
				persSym = cur.symbol;
			}
			cur = cur.parentelementid ? byId.get(cur.parentelementid) : null;
		}
		if (persSym) setPerspectiveFilter(persSym);
		if (objSym) setObjectiveFilter(objSym);
	}, [
		hierarchyElements,
		isEditMode,
		actionData?.searchKpiInitiativeId,
		isFromKpiDetails,
		kpiIdFromUrl,
		isFromInitiativeDetails,
		initiativeIdFromUrl,
		setScorecardFilter,
		setPerspectiveFilter,
		setObjectiveFilter,
	]);

	useEffect(() => {
		if (isEditMode && actionData) {
			const assignToArray: Group[] = (actionData.assignTo || []).map((id: string, index: number) => ({
				id,
				name: (actionData.assignToNames || [])[index] || "",
			})) as Group[];
			const searchKpiInitiative: KpiOrInitiative | null = actionData.searchKpiInitiativeId ? {
				id: actionData.searchKpiInitiativeId,
				typename: (actionData.searchKpiInitiativeType as "KPI" | "Initiative") || "KPI",
				name: actionData.searchKpiInitiativeName || "",
				displayName: actionData.searchKpiInitiativeName || "",
			} : null;

			const deadlineValue = actionData.deadline ? actionData.deadline : "-";

			setInitialValues({
				actionName: actionData.actionName || "",
				deadline: deadlineValue,
				description: actionData.description || "",
				assignTo: assignToArray,
				searchKpiInitiative,
				strategyKpiInitiative: searchKpiInitiative,
				status: actionData.status as ActionValues["status"] || "Not Started",
			});
		}
	}, [isEditMode, actionData]);

	const handleSubmit = async (values: ActionValues) => {
		setSubmitting(true);
		setError(null);

		try {
			const reqBody = {
				actionName: values.actionName,
				description: values.description,
				// deadline:values.deadline,
				assignTo: values.assignTo.map((item) => item.id),
				assignToNames: values.assignTo.map((item) => item.name),
				searchKpiInitiativeId: values.searchKpiInitiative?.id || null,
				searchKpiInitiativeName: values.searchKpiInitiative?.name || null,
				searchKpiInitiativeType: values.searchKpiInitiative?.typename || null,
				//strategyKpiInitiativeId: values.strategyKpiInitiative?.id || null,
				//strategyKpiInitiativeName: values.strategyKpiInitiative?.name || null,
				// strategyKpiInitiativeType: values.strategyKpiInitiative?.typename || null,
				status: values.status || "Not Started",
			};

			if (isEditMode && meaId) {
				await updateActionMutation.mutateAsync({ id: meaId as string, data: reqBody });
				setSuccessMessage("Action updated successfully!");
			} else {
				await createActionMutation.mutateAsync(reqBody);
				setSuccessMessage(t("Actions.Action created successfully"));
				history.push(`/${tenant}/${model}/actions`);
			}

			setIsEditing(false);
		} catch (err: any) {
			setError(err instanceof Error ? err.message : String(err));
		} finally {
			setSubmitting(false);
		}
	};

	const handleEdit = () => {
		setIsEditing(true);
		setSuccessMessage(null);
	};

	const handleCancel = () => {
		history.go(-1);
	};

	const handleStatusChange = async (newStatus: string) => {
		if (!meaId || isCreateMode) return;

		try {
			await updateStatusMutation.mutateAsync({ id: meaId as string, status: newStatus });
			setInitialValues((prev) => ({ ...prev, status: newStatus as ActionValues["status"] }));
			setSuccessMessage("Status updated successfully!");
		} catch (err: any) {
			setError(err instanceof Error ? err.message : "Failed to update status");
		}
	};

	const renderForm = () => (
		<div className={classes.scrollContent}>
			{error && (
				<div className={classes.error} role="alert">
					{error}
				</div>
			)}
			{successMessage && (
				<div style={{ color: "#2e7d32", marginBottom: "16px", fontWeight: 500 }}>
					{successMessage}
				</div>
			)}
			<Formik
				initialValues={initialValues}
				validationSchema={ValidationSchema}
				enableReinitialize
				onSubmit={handleSubmit}
			>
				{({
					  handleSubmit: formikSubmit,
					  handleChange,
					  handleBlur,
					  values,
					  errors,
					  setFieldValue,
					  touched,
				  }: FormikProps<ActionValues>) => (
					<form onSubmit={formikSubmit} className={classes.form}>
						{isEditMode && (
							<div className={classes.field}>
								<label className={classes.label} htmlFor="status">
									{t("Actions.Status")}
								</label>
								<Select
									fullWidth
									variant="outlined"
									id="status"
									name="status"
									value={values.status || "Not Started"}
									onChange={(e) => handleStatusChange(e.target.value as string)}
									disabled={updateStatusMutation.isLoading}
									className={classes.input}
								>
									<MenuItem value="Not Started">{t("Actions.Not Started")}</MenuItem>
									<MenuItem value="In Progress">{t("Actions.In Progress")}</MenuItem>
									<MenuItem value="Delayed">{t("Actions.Delayed")}</MenuItem>
									<MenuItem value="Completed">{t("Actions.Completed")}</MenuItem>
								</Select>
							</div>
						)}

						<div className={classes.field}>
							<label className={classes.label} htmlFor="actionName">
								{t("Actions.Action Name")} *
							</label>
							<input
								id="actionName"
								name="actionName"
								type="text"
								className={classes.input}
								value={values.actionName}
								onChange={handleChange}
								onBlur={handleBlur}
								disabled={!isEditing}
								required
							/>
							{touched.actionName && errors.actionName && (
								<span className={classes.fieldHint} style={{ color: "#d32f2f" }}>
                  {errors.actionName}
                </span>
							)}
						</div>

						<div className={classes.field}>
							<label className={classes.label} htmlFor="deadline">
								{t("Actions.Deadline")} *
							</label>
							<input
								id="deadline"
								name="deadline"
								type="date"
								className={classes.input}
								value={values.deadline}
								onChange={handleChange}
								onBlur={handleBlur}
								disabled={!isEditing}
								required
							/>
							{touched.deadline && errors.deadline && (
								<span className={classes.fieldHint} style={{ color: "#d32f2f" }}>
                  {errors.deadline}
                </span>
							)}
						</div>

						<div className={classes.field}>
							<label className={classes.label} htmlFor="description">
								{t("Actions.Description")}
							</label>
							<textarea
								id="description"
								name="description"
								className={classes.textarea}
								value={values.description}
								onChange={handleChange}
								onBlur={handleBlur}
								disabled={!isEditing}
								placeholder={t("Actions.Enter action description")}
							/>
						</div>

						<div className={classes.field}>
							<FormControl
								fullWidth
								variant="outlined"
								size="small"
								disabled={!isEditing || hierarchyLoading}
							>
								<InputLabel id="form-scorecard-label">
									{t("configtypes.Scorecard")}
								</InputLabel>
								<Select
									labelId="form-scorecard-label"
									label={t("configtypes.Scorecard")}
									displayEmpty
									value={scorecardFilter}
									onChange={(e) => {
										const v = e.target.value as string;
										setScorecardFilter(v);
										setFieldValue("searchKpiInitiative", null);
										setFieldValue("strategyKpiInitiative", null);
									}}
								>
									<MenuItem value="">
										<em>{t("Actions.All")}</em>
									</MenuItem>
									{geOptions.map((ge: any) => (
										<MenuItem key={ge.id} value={ge.id}>
											{getElementName(ge, ctx.language)}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</div>

						<div className={classes.field}>
							<FormControl
								fullWidth
								variant="outlined"
								size="small"
								disabled={
									!isEditing || hierarchyLoading || !scorecardFilter
								}
							>
								<InputLabel id="form-perspective-label">
									{t("configtypes.Perspective")}
								</InputLabel>
								<Select
									labelId="form-perspective-label"
									label={t("configtypes.Perspective")}
									displayEmpty
									value={perspectiveFilter}
									onChange={(e) => {
										const v = e.target.value as string;
										setPerspectiveFilter(v);
										setFieldValue("searchKpiInitiative", null);
										setFieldValue("strategyKpiInitiative", null);
									}}
								>
									<MenuItem value="">
										<em>{t("Actions.All")}</em>
									</MenuItem>
									{perspectiveSelectOptions.map(({ symbol, row }) => (
										<MenuItem key={symbol} value={symbol}>
											{getElementName(row, ctx.language)}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</div>

						<div className={classes.field}>
							<FormControl
								fullWidth
								variant="outlined"
								size="small"
								disabled={!isEditing || hierarchyLoading}
							>
								<InputLabel id="form-objective-label">
									{t("configtypes.Strategic Objective")}
								</InputLabel>
								<Select
									labelId="form-objective-label"
									label={t("configtypes.Strategic Objective")}
									displayEmpty
									value={objectiveFilter}
									onChange={(e) => {
										setObjectiveFilter(e.target.value as string);
										setFieldValue("searchKpiInitiative", null);
										setFieldValue("strategyKpiInitiative", null);
									}}
								>
									<MenuItem value="">
										<em>{t("Actions.All")}</em>
									</MenuItem>
									{objectiveSelectOptions.map(({ symbol, row }) => (
										<MenuItem key={symbol} value={symbol}>
											{getElementName(row, ctx.language)}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</div>

						<div className={classes.field}>
							<label className={classes.label} id="searchKpiInitiative-label">
								{t("Actions.Select a KPI or Initiative")}
							</label>
							<Autocomplete
								id="searchKpiInitiative"
								className={classes.groupsDropdown}
								options={filteredKpisAndInitiatives}
								value={
									isFromKpiDetails || isFromInitiativeDetails
										? {
												id: kpiIdFromUrl || initiativeIdFromUrl || "",
												typename: isFromKpiDetails
													? "KPI"
													: "Initiative",
												name:
													kpiNameFromUrl ||
													initiativeNameFromUrl ||
													"",
												displayName: `${
													kpiNameFromUrl ||
													initiativeNameFromUrl ||
													""
												} - ${
													isFromKpiDetails ? "KPI" : "Initiative"
												}`,
										  }
										: values.searchKpiInitiative
								}
								onChange={(_, newValue) => {
									setFieldValue("searchKpiInitiative", newValue);
									setFieldValue("strategyKpiInitiative", newValue);
								}}
								getOptionLabel={(option) => option.displayName}
								getOptionSelected={(option, value) =>
									option.id === value?.id
								}
								disabled={
									(isFromKpiDetails || isFromInitiativeDetails || !isEditing)
								}
								renderInput={(params) => (
									<TextField
										{...params}
										variant="outlined"
										size="small"
										placeholder={t(
											"Actions.Search Strategy Element (KPI/Initiative)"
										)}
									/>
								)}
							/>
						</div>

						<div className={classes.field}>
							<label className={classes.label} id="assignTo-label">
								{t("Actions.Assign To")}
							</label>
							<Autocomplete
								multiple
								id="assignTo"
								className={classes.groupsDropdown}
								options={users}
								value={values.assignTo}
								onChange={(_, newValue) => setFieldValue("assignTo", newValue)}
								getOptionLabel={(option) => option.name || option.pretty_name || option.fullname || ""}
								disabled={!isEditing}
								renderInput={(params) => (
									<TextField {...params} variant="outlined" size="small" />
								)}
							/>
						</div>

						<div className={classes.actions}>
							{isEditing ? (
								<>
									<button
										type="submit"
										className={`${classes.button} ${classes.buttonPrimary}`}
										disabled={submitting}
									>
										{submitting ? t("Actions.Saving") : t("Actions.Submit")}
									</button>
									<button
										type="button"
										className={`${classes.button} ${classes.buttonSecondary}`}
										onClick={handleCancel}
									>
										{t("Actions.Cancel")}
									</button>
								</>
							) : (
								<>
									<button
										type="button"
										className={`${classes.button} ${classes.buttonPrimary}`}
										onClick={handleEdit}
									>
										{t("Actions.Edit")}
									</button>
									<button
										type="button"
										className={`${classes.button} ${classes.buttonSecondary}`}
										onClick={handleCancel}
									>
										{t("Actions.Back")}
									</button>
								</>
							)}
						</div>
					</form>
				)}
			</Formik>
		</div>
	);

	if (isFullScreen) {
		return (
			<Dialog open={true} classes={{ paper: classes.dialogPaper }}>
				<DialogTitle className={classes.dialogTitle}>
					<span>{isEditMode ? t("Actions.Action Details") : t("Actions.Create New Action")}</span>
					<IconButton onClick={onClose} edge="end" aria-label="close">
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent className={classes.dialogContent}>{renderForm()}</DialogContent>
			</Dialog>
		);
	}

	return <>{renderForm()}</>;
};

const ActionsPage: React.FC = () => {
	const { meaId } = useParams<{ meaId?: string }>();
	const history = useHistory();
	const ctx = useAppContext();
	const tenant = ctx.tenant || "tec";
	const model = ctx.modelId || ctx.model;

	const isCreateMode = meaId === "new";
	const isEditMode = meaId && meaId !== "new";
	const isFormMode = isCreateMode || isEditMode;

	if (isFormMode) {
		return (
			<Page
				Toolbar={() => (
					<GenericToolbar
						title={
							isCreateMode
								? "Actions.Create Action"
								: "Actions.Action Details"
						}
						icon="/icons/navitems/workflows.svg"
						hidePeriodSelector={true}
					/>
				)}
			>
				<ActionsForm
					isFullScreen={false}
					onClose={() => history.push(`/${tenant}/${model}/actions`)}
				/>
			</Page>
		);
	}

	return (
		<Page
			Toolbar={() => (
				<GenericToolbar
					title="Actions.Actions"
					icon="/icons/navitems/workflows.svg"
					hidePeriodSelector={true}
				/>
			)}
		>
			<ActionsList
				onRowClick={(id) => history.push(`/${tenant}/${model}/actions/${id}`)}
				onNewClick={() => history.push(`/${tenant}/${model}/actions/new`)}
			/>
		</Page>
	);
};

export { ActionsPage };
export default ActionsPage;

